<template>
  <AdminLayout>
    <Head title="Suppliers" />

    <template #pageTitle>Suppliers</template>

    <template #headingOptions>
      <button
        @click="showModal = true"
        type="button"
        class="inline-flex items-center px-4 py-3 mr-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
      >
        <PlusSmIconSolid
          class="-ml-0.5 mr-2 h-4 w-4"
          aria-hidden="true"
        />
        Create Supplier
      </button>
    </template>

    <DataGrid
      name="suppliers_data_table"
      :endpoint="route('api.tables.suppliers')"
      rowId="id"
      :filters="form"
      :columns="columns"
      :block-size="40"
      :page-size="40"
    />

    <template #modals>
      <SmallModal
        :is-open="showModal"
        @close="closeModal"
      >
        <template #header>Create Supplier</template>
        <div class="mt-4">
          <div class="input-group">
            <label for="name">Name</label>
            <input
              type="text"
              v-model="supplierForm.name"
              name="name"
              id="name"
            />
            <p
              v-if="getErrors('name')"
              class="input-error"
            >
              {{ getErrors('name') }}
            </p>
          </div>
          <div class="input-group">
            <label for="license_number">License Number</label>
            <input
              type="text"
              v-model="supplierForm.license_number"
              name="license_number"
              id="license_number"
            />
            <p
              v-if="getErrors('license_number')"
              class="input-error"
            >
              {{ getErrors('license_number') }}
            </p>
          </div>
        </div>
        <template #footer>
          <button
            type="button"
            @click="createSupplier"
            :disabled="formSubmitting"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
          >
            Create
          </button>
          <button
            type="button"
            class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-gray-100 text-base font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 sm:ml-3 sm:w-auto sm:text-sm"
            @click="closeModal"
          >
            Cancel
          </button>
        </template>
      </SmallModal>
    </template>
  </AdminLayout>
</template>

<script setup>
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { PlusSmIcon as PlusSmIconSolid } from '@heroicons/vue/solid'
import { Head, useForm } from '@inertiajs/vue3'
import DataGrid from '@/Components/DataGrid/Tables/Generic.vue'
import LinkCell from '@/Components/DataGrid/Cells/LinkCell'
import SmallModal from '@/Components/Modals/SmallModal.vue'
import { useInitGridState } from '@/Components/DataGrid/Composables/grid-state'
import { ref } from 'vue'

const props = defineProps({
  errors: Object
})

useInitGridState()

const supplierForm = useForm({
  name: '',
  license_number: ''
})

const showModal = ref(false)

const columns = ref([
  {
    headerName: 'Name',
    field: 'name',
    cellRenderer: LinkCell,
    cellRendererParams: (params) => ({
      url: `/company/suppliers/${params.data.id}/edit`,
      display: params.value
    }),
    filter: 'agTextColumnFilter',
    sort: 'asc'
  },
  {
    headerName: 'License Number',
    field: 'license_number',
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'City',
    field: 'contact.city',
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'State',
    field: 'contact.state',
    filter: 'agTextColumnFilter'
  }
])

const form = ref({})
const formSubmitting = ref(false)

const createSupplier = () => {
  formSubmitting.value = true
  supplierForm.post(route('company.suppliers.store'), {
    onSuccess: () => {
      closeModal()
    },
    onFinish: () => {
      formSubmitting.value = false
    }
  })
}

const closeModal = () => {
  showModal.value = false
  supplierForm.reset()
}

const getErrors = (field, bag = null) => {
  let errors = bag ? props.errors[bag] || {} : props.errors

  if (field in errors) {
    return errors[field]
  }

  return ''
}
</script>
